import { useState, useMemo, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import DSBox from './design-system/DSBox';
import NavigationDrawer from './components/navigationDrawer/NavigationDrawer';
import { NavigationDrawerItem } from './types';
import Dashboard from './views/Dashboard/components/Dashboard';
import Klydos from './views/Klydos/KlydosList/components/Klydos';
import CalendarEvents from './views/Calendar/components/CalendarEvents';
import AppBar from './components/AppBar/components/AppBar';
import { useTheme } from '@mui/material';
import { APPBAR_HIGHT, DRAWER_WIDTH } from './consts/general';
import { AlertType } from './design-system/DSAlert';
import useAppContext from './hooks/useAppContext';
import ContractAgreementModal from './components/ContractAgreementModal';
import OnboardingWizard from './components/OnboardingWizard';
import Help from './views/Help';
import Collections from './views/Collections/components/Collections';
import usePaymentStatusQuery from './hooks/usePaymentStatusQuery';
import { PayoutStatus } from './api/payment';
import { PAYMENT_PENDING_ALERT } from './consts/alerts';
import useStatusNotifications from './hooks/useStatusNotifications';

const PAYMENT_URL = 'https://payment.klydoclock.com';

const baseNavigationDrawerPages: NavigationDrawerItem[] = [
  { title: 'Dashboard', route: '/dashboard', element: <Dashboard /> },
  { title: 'Klydos', route: '/klydos', element: <Klydos /> },
  {
    title: 'Collections',
    route: '/collections',
    element: <Collections />,
  },
  {
    title: 'Feed Scheduler',
    route: '/calendar-events',
    element: <CalendarEvents />,
  },
  {
    title: 'Payment',
    route: '/payment',
    externalUrl: PAYMENT_URL,
  },

  { title: 'Help', route: '/help', element: <Help /> },
];

const MainLayout = () => {
  const { spacing, palette } = useTheme();
  const { showAlert, hasAgreedToContract, isAdmin } = useAppContext();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const { data: paymentStatus } = usePaymentStatusQuery(
    hasAgreedToContract || isAdmin,
  );
  const { hasNewKlydoStatus, hasNewCollectionStatus } =
    useStatusNotifications();

  const navigationDrawerPages = useMemo(
    () =>
      baseNavigationDrawerPages.map((page) => {
        if (page.route === '/payment') {
          return {
            ...page,
            showNotification:
              paymentStatus != null && !paymentStatus.isSetupComplete,
          };
        }
        if (page.route === '/klydos') {
          return { ...page, showNotification: hasNewKlydoStatus };
        }
        if (page.route === '/collections') {
          return { ...page, showNotification: hasNewCollectionStatus };
        }
        return page;
      }),
    [paymentStatus, hasNewKlydoStatus, hasNewCollectionStatus],
  );

  useEffect(() => {
    if (paymentStatus?.lastPaymentStatus === PayoutStatus.Pending) {
      showAlert({
        alertType: AlertType.WARNING,
        message: PAYMENT_PENDING_ALERT,
      });
    }
  }, [paymentStatus]);

  if (!hasAgreedToContract && !isAdmin) {
    if (!hasCompletedOnboarding) {
      return (
        <OnboardingWizard onComplete={() => setHasCompletedOnboarding(true)} />
      );
    }
    return <ContractAgreementModal />;
  }
  return (
    <DSBox
      sx={{
        backgroundColor: palette.background.default,
      }}
    >
      <NavigationDrawer pages={navigationDrawerPages} />
      <AppBar />

      <DSBox
        sx={{
          flexGrow: 1,
          paddingX: { sm: '1.1%' },
          marginTop: APPBAR_HIGHT,
          marginLeft: { lg: spacing(DRAWER_WIDTH) },
          backgroundColor: palette.background.default,
        }}
      >
        <Outlet />
      </DSBox>
    </DSBox>
  );
};

export default MainLayout;
